'use client';

import { RefObject, useCallback, useEffect, useId, useRef, useState } from 'react';
import useEscapeKeydown from '@/hooks/useEsacapeKeydown';
import { UserIcon } from '@/assets/icons/UserIcon';
import { DownloadButtonProps } from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import {
    SignedInMenu,
    type SignedInMenuAction,
    type SignedInMenuProps,
} from '@/ui-components/organisms/Snapshot/SignedInMenu/SignedInMenu';
import { CARE_ACCOUNT_URLS } from './careAccountUrls';
import { SnapshotPopoverShell } from './SnapshotPopoverShell';
import {
    SnapshotAuthenticatedUser,
    SnapshotUserPopover,
    TeslaManageSelectionData,
} from './SnapshotUserPopover';
import type { SnapshotSubscriptionState } from './useSnapshotSubscription';
import { announceNavFlyoutOpen, useCloseOnOtherNavFlyoutOpen } from './navFlyoutEvents';
import styles from './snapshotpopover.module.scss';

interface AnonymousUserSnapshotIconProps {
    appdownloadItem?: DownloadButtonProps[];
    authenticatedUser?: SnapshotAuthenticatedUser | null;
    isRestoringAuth?: boolean;
    onOpen?: () => void;
    onAuthenticated?: (user: SnapshotAuthenticatedUser) => void;
    onSignOut?: () => void;
    subscriptionState?: SnapshotSubscriptionState | null;
    teslaManageSelection?: TeslaManageSelectionData;
}

const LINKNAME_PREFIX = 'AccountSnapshot:User:';

const COMMON_ACCOUNT_ACTIONS: SignedInMenuAction[] = [
    {
        label: 'Go to billing',
        href: CARE_ACCOUNT_URLS.billing,
        ariaLabel: 'Go to billing',
        dataLinkname: `${LINKNAME_PREFIX}GoToBilling`,
    },
    {
        label: 'Make payment or update info',
        href: CARE_ACCOUNT_URLS.makePayment,
        ariaLabel: 'Make payment or update info',
        dataLinkname: `${LINKNAME_PREFIX}MakePayment`,
    },
];

function buildAccountActions(
    subscriptionState: SnapshotSubscriptionState | null | undefined,
): SignedInMenuAction[] {
    // Until subscription details resolve, render the streaming (smaller)
    // variant so we never show actions the visitor cannot use.
    if (!subscriptionState || subscriptionState.variant === 'streaming') {
        return COMMON_ACCOUNT_ACTIONS;
    }

    return [
        ...COMMON_ACCOUNT_ACTIONS,
        {
            label: 'Request Radio Signal',
            href: subscriptionState.refreshRadioHref,
            ariaLabel: 'Request Radio Signal',
            dataLinkname: `${LINKNAME_PREFIX}RequestRadioSignal`,
        },
        {
            label: 'Transfer Subscription',
            href: subscriptionState.transferSubscriptionHref,
            ariaLabel: 'Transfer Subscription',
            dataLinkname: `${LINKNAME_PREFIX}TransferSubscription`,
        },
    ];
}

export function AnonymousUserSnapshotIcon({
    appdownloadItem,
    authenticatedUser = null,
    isRestoringAuth = false,
    onOpen,
    onAuthenticated,
    onSignOut,
    subscriptionState = null,
    teslaManageSelection,
}: AnonymousUserSnapshotIconProps): React.JSX.Element {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverId = useId();

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    useCloseOnOtherNavFlyoutOpen('account', handleClose);

    const handleToggle = useCallback(() => {
        if (!open) {
            announceNavFlyoutOpen('account');
            onOpen?.();
        }
        setOpen(!open);
    }, [open, onOpen]);

    const handleAuthenticated = useCallback(
        (user: SnapshotAuthenticatedUser) => {
            onAuthenticated?.(user);
        },
        [onAuthenticated],
    );

    const handleIconClick = useCallback(() => {
        (window as BrowserWindow).pushToAdobe({
            event: 'customer-info',
            customerInfo: {
                authenticationStatus: authenticatedUser !== null ? 'Authenticated users' : 'Unidentified users',
            },
        });
    }, [authenticatedUser]);

    useEffect(() => {
        const button = buttonRef.current;
        if (!button) return;
        button.addEventListener('click', handleIconClick, true);
        return () => button.removeEventListener('click', handleIconClick, true);
    }, [handleIconClick]);

    // Suppress the signed-in menu while the Tesla partner-subscription flow
    // is active — that flow takes over the popover via SnapshotUserPopover.
    const signedInMenu: SignedInMenuProps | null = authenticatedUser && !teslaManageSelection
        ? {
              userName: authenticatedUser.userName,
              accountNumber: authenticatedUser.accountNumber,
              primaryAction: {
                  label: 'Manage my account',
                  href: CARE_ACCOUNT_URLS.manageAccount,
                  icon: 'external',
                  ariaLabel: 'Manage my account',
                  dataLinkname: `${LINKNAME_PREFIX}ManageAccount`,
              },
              accountActions: buildAccountActions(subscriptionState),
              signOutAction: {
                  label: 'Sign out',
                  ariaLabel: 'Sign out',
                  dataLinkname: `${LINKNAME_PREFIX}SignOut`,
                  onSelect: async () => {
                      const browserWindow = window as BrowserWindow;

                      try {
                          await browserWindow.clientSdk?.user.signOut();
                      } catch {}

                      setOpen(false);
                      onSignOut?.();
                  },
              },
              appDownloadItems: appdownloadItem ?? [],
          }
        : null;

    useEscapeKeydown(handleClose);

    return (
        <div className={styles.snapshotTrigger}>
            <button
                ref={buttonRef}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-controls={popoverId}
                aria-label={open ? 'Close account menu' : 'Open account menu'}
                className={styles.snapshotIconButton}
                data-linkname={open ? `${LINKNAME_PREFIX}Close` : `${LINKNAME_PREFIX}Open`}
                onClick={handleToggle}
                type="button"
            >
                <UserIcon />
            </button>

            {signedInMenu ? (
                <SnapshotPopoverShell
                    ariaLabel="Signed in menu"
                    buttonRef={buttonRef as RefObject<HTMLButtonElement | null>}
                    onClose={handleClose}
                    open={open}
                >
                    <SignedInMenu {...signedInMenu} />
                </SnapshotPopoverShell>
            ) : !isRestoringAuth ? (
                <SnapshotUserPopover
                    appdownloadItem={appdownloadItem}
                    buttonRef={buttonRef as RefObject<HTMLButtonElement | null>}
                    onAuthenticated={handleAuthenticated}
                    id={popoverId}
                    initialTeslaManageSelection={teslaManageSelection}
                    initialStep={teslaManageSelection ? 'partnerSubscription' : 'enterIdentifier'}
                    onClose={handleClose}
                    open={open}
                />
            ) : null}
        </div>
    );
}
